import { DiagnosticReport, ImagingStudy } from 'fhir/r4';

export interface ImagingStudyLocalImage {
    id: string;
    fileName: string;
    contentType: string;
    size: number;
    createdAt: string;
    dataUrl: string;
    base64: string;
    selected: boolean;
    fileLink?: string;
    azureBlobName?: string;
}

export const DEFAULT_IMAGING_STUDY_IMAGE_EXTENSION_URL =
    'https://elikita-server.daalitech.com/StructureDefinition/imagingStudyImage';

function getSubExtensionValue<T>(ext: any, url: string): T | undefined {
    const sub = (ext?.extension || []).find((e: any) => e?.url === url);
    if (!sub) return undefined;

    return (
        sub.valueString ??
        sub.valueInteger ??
        sub.valueBoolean ??
        sub.valueDateTime ??
        sub.valueBase64Binary
    ) as T;
}

export function imagesToImagingStudyExtensions(
    images: ImagingStudyLocalImage[],
    canonicalUrl: string = DEFAULT_IMAGING_STUDY_IMAGE_EXTENSION_URL
): any[] {
    return (images || []).map((img) => ({
        url: canonicalUrl,
        extension: [
            { url: 'id', valueString: img.id },
            { url: 'fileName', valueString: img.fileName },
            { url: 'contentType', valueString: img.contentType },
            { url: 'size', valueInteger: img.size },
            { url: 'createdAt', valueDateTime: img.createdAt },
            { url: 'selected', valueBoolean: !!img.selected },
            { url: 'data', valueBase64Binary: img.base64 },
            // Optional cloud metadata
            ...(img as any).fileLink ? [{ url: 'fileLink', valueString: (img as any).fileLink }] : [],
            ...(img as any).azureBlobName ? [{ url: 'azureBlobName', valueString: (img as any).azureBlobName }] : []
        ]
    }));
}

export function imagingStudyExtensionsToImages(
    extensions: any[],
    canonicalUrl: string = DEFAULT_IMAGING_STUDY_IMAGE_EXTENSION_URL
): ImagingStudyLocalImage[] {
    const list = (extensions || []).filter((e: any) => e?.url === canonicalUrl);
    return list
        .map((ext: any) => {
            const id = getSubExtensionValue<string>(ext, 'id') || crypto.randomUUID?.() || String(Date.now());
            const fileName = getSubExtensionValue<string>(ext, 'fileName') || 'image';
            const contentType = getSubExtensionValue<string>(ext, 'contentType') || 'image/*';
            const size = Number(getSubExtensionValue<number>(ext, 'size') || 0);
            const createdAt = getSubExtensionValue<string>(ext, 'createdAt') || new Date().toISOString();
            const selected = !!getSubExtensionValue<boolean>(ext, 'selected');
            const base64 = (getSubExtensionValue<string>(ext, 'data') || '').toString();
            const dataUrl = base64 ? `data:${contentType};base64,${base64}` : '';
            const fileLink = getSubExtensionValue<string>(ext, 'fileLink') || '';
            const azureBlobName = getSubExtensionValue<string>(ext, 'azureBlobName') || '';
            return { id, fileName, contentType, size, createdAt, selected, base64, dataUrl, fileLink, azureBlobName } as any;
        })
        // keep entries even if base64 is empty (cloud-only entries preserved)
        .filter(Boolean) as ImagingStudyLocalImage[];
}

export function upsertImagingStudyImages(
    study: ImagingStudy,
    images: ImagingStudyLocalImage[],
    canonicalUrl: string = DEFAULT_IMAGING_STUDY_IMAGE_EXTENSION_URL
): ImagingStudy {
    const existing = Array.isArray((study as any)?.extension) ? ((study as any).extension as any[]) : [];
    const kept = existing.filter((e: any) => e?.url !== canonicalUrl);
    const updated = kept.concat(imagesToImagingStudyExtensions(images || [], canonicalUrl));
    return { ...(study as any), extension: updated } as ImagingStudy;
}

export function selectedImagesToDiagnosticReportPresentedForms(images: ImagingStudyLocalImage[]): DiagnosticReport['presentedForm'] {
    const selected = (images || []).filter((i) => i?.selected);
    return selected.map((i) => ({
        contentType: i.contentType,
        data: i.base64,
        title: i.fileName,
        size: i.size,
        creation: i.createdAt
    })) as any;
}
